import React from "react";

import {
  faArchive,
  faCopy,
  faEye,
  faHistory,
} from "@fortawesome/pro-regular-svg-icons";
import { useNavigate } from "react-router-dom";

import { ConfirmationModal } from "src/base-components/ConfirmationModal";
import { FeatureQueryStatus } from "src/clients/features-control";
import { IconButton } from "src/design-system/Icon";
import { useModal } from "src/design-system/Modal";
import { toastActions } from "src/design-system/Toast/utils";
import { Tooltip } from "src/design-system/Tooltip";
import { BackfillQueryModal } from "src/featureCatalogue/Queries/BackfillQueryModal";
import { useArchiveFeatureQuery } from "src/featureCatalogue/queries";
import { Query } from "src/featureCatalogue/utils";
import { useCapabilities } from "src/hooks/useCapabilities";
import {
  DashboardPageParams,
  getUrlToManageFeatureQueryPage,
} from "src/router/urls";
import { errorMessage } from "src/utils/stringUtils";
import { useParamsDecode } from "src/utils/useParamsDecode";

type TableActionsProps = {
  query: Query | undefined;
  showViewQuery?: boolean;
};

export const TableActions: React.FC<TableActionsProps> = ({
  query,
  showViewQuery = false,
}) => {
  const navigate = useNavigate();
  const { orgId, wsId } = useParamsDecode<DashboardPageParams>();

  const {
    isOpen: isBackfillOpen,
    openModal: openBackfill,
    closeModal: closeBackfill,
    afterLeave: afterBackfillLeave,
  } = useModal();

  const {
    isOpen: isArchiveOpen,
    openModal: openArchive,
    closeModal: closeArchive,
    afterLeave: afterArchiveLeave,
  } = useModal();

  const { mutateAsync: archiveFeatureQuery } = useArchiveFeatureQuery();
  const { featureQueriesCatalogue } = useCapabilities();

  const archiveQuery = async () => {
    if (!query) return;
    try {
      await archiveFeatureQuery({
        id: query.id,
        etag: query.etag,
      });
      closeArchive();
      toastActions.success({
        title: "Query Archived",
        description: "Query is successfully archived.",
      });
    } catch (error) {
      const message = errorMessage(error);
      toastActions.failure({
        title: "Archive Failed",
        description: message || "Query couldn't be archived. Try again.",
      });
    }
  };

  const hasQuery = !!query?.id;
  const isQueryActiveInAnyEnvironment = Object.values(
    query?.environment_settings ?? {},
  ).some((setting) => setting.status === FeatureQueryStatus.ACTIVE);
  const options = [
    {
      icon: faHistory,
      action: openBackfill,
      title: "Backfill",
      canAccess:
        !showViewQuery && featureQueriesCatalogue.canBackfill && hasQuery,
    },
    {
      icon: faCopy,
      action: () =>
        navigate(
          getUrlToManageFeatureQueryPage(orgId, wsId, query?.id, "duplicate"),
        ),
      title: "Duplicate",
      canAccess:
        !showViewQuery && featureQueriesCatalogue.canDuplicate && hasQuery,
    },
    {
      icon: faEye,
      action: () =>
        navigate(
          getUrlToManageFeatureQueryPage(orgId, wsId, query?.id, "view"),
        ),
      title: "View Query",
      canAccess: featureQueriesCatalogue.canViewQueryDetails && hasQuery,
    },
    {
      icon: faArchive,
      action: openArchive,
      title: "Archive",
      canAccess: !showViewQuery && featureQueriesCatalogue.canArchive,
      disabled: isQueryActiveInAnyEnvironment,
      disabledTooltipTitle: "Cannot archive active Queries",
      disabledTooltipBody:
        "To archive a Query, set Sandbox and Live statuses to Inactive",
    },
  ];

  return (
    <>
      <div
        className="flex cursor-pointer items-center gap-x-3 opacity-0 transition-opacity group-hover:opacity-100"
        onClick={(e) => e.stopPropagation()}
      >
        {options
          .filter((option) => option.canAccess !== false)
          .map((option) => (
            <Tooltip
              key={option.title}
              body={option.disabled ? option.disabledTooltipBody : undefined}
              title={
                option.disabled ? option.disabledTooltipTitle : option.title
              }
              asChild
            >
              <IconButton
                disabled={option.disabled}
                icon={option.icon}
                size="xs"
                onClick={option.action}
                data-loc={`query-actions-${option.title.toLowerCase()}`}
              />
            </Tooltip>
          ))}
      </div>

      {/* Prevent modal backdrop clicks from bubbling to the table row */}
      <div onClick={(e) => e.stopPropagation()}>
        <BackfillQueryModal
          afterLeave={afterBackfillLeave}
          open={isBackfillOpen}
          query={query}
          onClose={closeBackfill}
        />

        <ConfirmationModal
          afterLeave={afterArchiveLeave}
          confirmationButtonText="Archive"
          description="Are you sure you want to archive this query? This action cannot be undone."
          open={isArchiveOpen}
          title="Archive Query?"
          variant="danger"
          onClose={closeArchive}
          onConfirm={archiveQuery}
        />
      </div>
    </>
  );
};
